import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { TrialGroupItemTemplatesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsTrialSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as GroupActions from "../../../actions/groups";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as TrialGroupItemTemplateActions from "../../../actions/trialGroupItemTemplate";
import { FormFilterInput, SelectFormInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    findLookup,
    generateOptionsFromLookup,
} from "../../../helpers/lookupHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import TrialGroupLookupsLayout from "../TrialGroupLookupsLayout/TrialGroupLookupsLayout";

import "./TrialGroupItemTemplatesPage.scss";

interface TrialGroupItemTemplatesPageProps {
    groups: Dtos.Group[];
    loadingGroups: boolean;
    loadGroupsSuccess: boolean;
    loadGroupsFailure: boolean;
    loadGroupsErrorCode: string;
    trialGroupItemTemplates: Dtos.TrialGroupItemTemplate[];
    trialGroupItemsByTrialGroupItemTemplateCategory: Dtos.TrialGroupItemTemplateCategory[];
    formFilter: IFormFilter;
    lookups: Dtos.Lookup[];
    loadingTrialGroupItemTemplates: boolean;
    loadTrialGroupItemTemplatesSuccess: boolean;
    loadTrialGroupItemTemplatesFailure: boolean;
    loadTrialGroupItemTemplatesErrorCode: string;
    user: Dtos.User;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearGroups: GroupActions.IGroupClearActionCreator;
    loadGroups: GroupActions.IGroupLoadActionCreator;
    clearTrialGroupItemTemplates: TrialGroupItemTemplateActions.ITrialGroupItemTemplateClearActionCreator;
    loadTrialGroupItemTemplates: TrialGroupItemTemplateActions.ITrialGroupItemTemplateLoadActionCreator;
    filter: TrialGroupItemTemplateActions.ITrialGroupItemTemplateFilterActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialGroupItemTemplatesPage: FC<TrialGroupItemTemplatesPageProps> = ({
    groups,
    loadingGroups,
    loadGroupsFailure,
    loadGroupsErrorCode,
    trialGroupItemTemplates,
    trialGroupItemsByTrialGroupItemTemplateCategory,
    formFilter,
    lookups,
    loadingTrialGroupItemTemplates,
    loadTrialGroupItemTemplatesSuccess,
    loadTrialGroupItemTemplatesFailure,
    loadTrialGroupItemTemplatesErrorCode,
    permissions,
    clearGroups,
    loadGroups,
    clearTrialGroupItemTemplates,
    loadTrialGroupItemTemplates,
    filter,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{
        groupId: string;
    }>();
    const groupIdNumber = parseInt(groupId, 10);

    //
    // -- States --
    const [groupIdState, setGroupIdState] = useState<number | null>(
        groupIdNumber
    );
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const loading = loadingTrialGroupItemTemplates || loadingGroups;
    const showTrialItems =
        !loadingTrialGroupItemTemplates &&
        loadTrialGroupItemTemplatesSuccess &&
        !creatingTree;


    // Load Groups & Items
    useEffect(() => {
        setDrawerItems(SettingsTrialSubMenu(permissions));
        setBreadcrumbs(
            TrialGroupItemTemplatesBreadcrumbs(
                isNaN(groupIdNumber) ? undefined : groupIdNumber
            )
        );

        // Dropdown options
        if (!groups || groups.length === 0) {
            loadGroups();
        }

        // Load templates
        if (
            groupIdState &&
            !loadingTrialGroupItemTemplates &&
            !loadTrialGroupItemTemplatesSuccess
        ) {
            loadTrialGroupItemTemplates(groupIdState);
        }
    }, [
        loadGroups,
        setDrawerItems,
        SettingsTrialSubMenu,
        setBreadcrumbs,
        TrialGroupItemTemplatesBreadcrumbs,
        groups,
        groupIdState,
        trialGroupItemTemplates,
        permissions,
        loadTrialGroupItemTemplatesSuccess,
        loadTrialGroupItemTemplates,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadGroupsFailure) {
            navigate(`/error/${loadGroupsErrorCode}`);
        }
        if (loadTrialGroupItemTemplatesFailure) {
            navigate(`/error/${loadTrialGroupItemTemplatesErrorCode}`);
        }
    }, [
        loadGroupsFailure,
        navigate,
        loadTrialGroupItemTemplatesFailure,
        loadGroupsErrorCode,
        loadTrialGroupItemTemplatesErrorCode,
    ]);

    // Cleanup effect on component unmount
    useEffect(() => {
        return () => {
            clearGroups();
            clearTrialGroupItemTemplates();
        };
    }, [clearGroups, clearTrialGroupItemTemplates]);

    // Dropdown options memos
    const groupSelectOptions = useMemo(() => {
        if (!groups) return [];

        return groups
            ?.filter((x) => x.name)
            ?.sort((a, b) => a?.name.localeCompare(b?.name))
            ?.map((x) => ({ value: x.id, label: x?.name }));
    }, [groups]);

    // Reset tree initalisation on form filter change
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [
        trialGroupItemsByTrialGroupItemTemplateCategory,
        formFilter,
        setInitalizedTreeData,
        groupId,
    ]);

    // Create tree
    useEffect(() => {
        if (
            trialGroupItemsByTrialGroupItemTemplateCategory &&
            !initalizedTreeData
        ) {
            setCreatingTree(true);

            // If no trial group items return
            if (
                !trialGroupItemsByTrialGroupItemTemplateCategory ||
                trialGroupItemsByTrialGroupItemTemplateCategory?.length == 0
            ) {
                setTreeData(null);
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (trialGroupItems) => {
                let children = [];

                children =
                    trialGroupItems
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `item-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: "1fr 4fr 4fr 3fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.FILE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.name}</div>
                                    <div className="d-inline-block px-1 text-truncate">
                                        <span
                                            className="text-truncate"
                                            data-tip={y?.description}
                                        >
                                            {y?.description}
                                            <ReactTooltip />
                                        </span>
                                    </div>
                                    <div className="px-1">
                                        {y?.initialVersion}
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 4fr 4fr 3fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Name</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Description</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Initial Version</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                trialGroupItemsByTrialGroupItemTemplateCategory
                    ?.sort((a, b) => a?.rank - b?.rank)
                    ?.map((x) => ({
                        id: `category-${x.id}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.ROLE}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "400px" }}
                                >
                                    {x?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary ml-4 mb-0">
                                        {x?.trialGroupItemTemplates?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [
                                ...createChildNodes(x.trialGroupItemTemplates),
                            ],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        trialGroupItemsByTrialGroupItemTemplateCategory,
        initalizedTreeData,
        treeData,
        formFilter,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("item") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.TrialGroupItemTemplatesView
                    )
                ) {
                    navigate(
                        groupIdState
                            ? `/settings/trialgroup/itemtemplates/${groupIdState}/template/${id.replace(
                                  "item-",
                                  ""
                              )}`
                            : `/settings/trialgroup/itemtemplates/template/${id.replace(
                                  "item-",
                                  ""
                              )}`
                    );
                }
                if (id.includes("category")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [setToggleState, navigate, permissions]
    );

    const pageContent = useMemo(() => {
        return (
            <>
                <div className="row">
                    <div className={"col-md-12 col-lg-auto mb-2"}>
                        <h1>Trial Group Item Templates</h1>
                    </div>
                    <div
                        className="col-md-12 col-lg d-flex justify-content-md-start justify-content-lg-end align-items-stretch mb-md-2 mb-lg-0"
                        style={{ height: "38px" }}
                    >
                        <div
                            className="d-inline-table pr-2"
                            style={{ width: "300px" }}
                        >
                            <SelectFormInput
                                loading={loadingGroups}
                                multi={false}
                                options={groupSelectOptions}
                                clearable={true}
                                backspaceRemoves={false}
                                name={"groupIdState"}
                                id={"groupIdState"}
                                placeholder="Group"
                                onChange={function (
                                    value: any,
                                    _options?: any
                                ): any {
                                    clearTrialGroupItemTemplates();
                                    setGroupIdState(
                                        value != undefined
                                            ? parseInt(value)
                                            : null
                                    );
                                    return undefined;
                                }}
                                value={groupIdState}
                            />
                        </div>
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.TrialGroupItemTemplatesCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={
                                    groupIdState
                                        ? `/settings/trialgroup/itemtemplates/${groupIdState}/template/create`
                                        : "/settings/trialgroup/itemtemplates/template/create"
                                }
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {loading ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {!groupIdState ? (
                    <div className="card card-shadow p-3">
                        <div className="patient-tables">
                            <div className="alert alert-info d-flex mb-0">
                                <div className="mr-2">
                                    <FontAwesomeIcon
                                        icon={
                                            FontAwesomeIcons.Solid.INFO_SQUARE
                                        }
                                        fixedWidth
                                    />
                                </div>
                                <span>
                                    Select a group to view item templates.
                                </span>
                            </div>
                        </div>
                    </div>
                ) : null}
                {showTrialItems && groupIdState && !loading ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no items to view.
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </>
        );
    }, [
        generateOptionsFromLookup,
        findLookup,
        lookups,
        showTrialItems,
        groupIdState,
        setGroupIdState,
        trialGroupItemTemplates,
        groupSelectOptions,
        loadingGroups,
        clearTrialGroupItemTemplates,
        treeData,
        handleToggle,
        toggleState,
        loading,
    ]);

    return (
        <TrialGroupLookupsLayout
            permissions={permissions}
            permission={Dtos.Permission.TrialGroupItemTemplatesView}
        >
            {pageContent}
        </TrialGroupLookupsLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialGroupItemTemplatesPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        groups: state.groups.formFilteredData,
        loadingGroups:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Pending,
        loadGroupsSuccess:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Success,
        loadGroupsFailure:
            state.groups.loadState &&
            state.groups.loadState.status == RequestState.Failure,
        loadGroupsErrorCode: state.groups.loadState.errorCode,
        trialGroupItemTemplates: state.trialGroupItemTemplate.formFilteredData!,
        trialGroupItemsByTrialGroupItemTemplateCategory:
            state.trialGroupItemTemplate.formFilteredDataGrouped,
        formFilter: state.trialGroupItemTemplate.formFilter,
        lookups: state.trialGroupItemTemplate.lookups,
        loadingTrialGroupItemTemplates:
            state.trialGroupItemTemplate.loadState &&
            state.trialGroupItemTemplate.loadState.status ==
                RequestState.Pending,
        loadTrialGroupItemTemplatesSuccess:
            state.trialGroupItemTemplate.loadState &&
            state.trialGroupItemTemplate.loadState.status ==
                RequestState.Success,
        loadTrialGroupItemTemplatesFailure:
            state.trialGroupItemTemplate.loadState &&
            state.trialGroupItemTemplate.loadState.status ==
                RequestState.Failure,
        loadTrialGroupItemTemplatesErrorCode:
            state.trialGroupItemTemplate.loadState.errorCode,
        user: state.user.data,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialGroupItemTemplatesPageProps> => {
    return {
        loadGroups: bindActionCreators(GroupActions.LoadGroup, dispatch),
        clearGroups: bindActionCreators(GroupActions.Clear, dispatch),
        loadTrialGroupItemTemplates: bindActionCreators(
            TrialGroupItemTemplateActions.LoadTrialGroupItemTemplate,
            dispatch
        ),
        clearTrialGroupItemTemplates: bindActionCreators(
            TrialGroupItemTemplateActions.Clear,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        filter: bindActionCreators(
            TrialGroupItemTemplateActions.Filter,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialGroupItemTemplatesPage);
